import { SegmentProgress } from "@/components/staff/SegmentProgress";
import { dateAndTime, shortDate } from "@/lib/staff/format";
import type {
  StaffRoomDetailSegment,
  StaffRoomDetailMember,
  StaffMargauxPost,
  StaffSchedulerLogEntry,
  StaffReflection,
} from "@/lib/staff/types";
import styles from "./sections.module.scss";

function segmentRange(seg: StaffRoomDetailSegment): string {
  if (seg.startPage && seg.endPage) {
    return `pp. ${seg.startPage}–${seg.endPage}`;
  }
  if (seg.startChapter && seg.endChapter) {
    return `ch. ${seg.startChapter}–${seg.endChapter}`;
  }
  return "—";
}

export function SegmentSection({
  segments,
  currentSegment,
  status,
}: {
  segments: StaffRoomDetailSegment[];
  currentSegment: number;
  status: string;
}) {
  return (
    <section className={styles.card} aria-labelledby="segments-heading">
      <header className={styles.cardHead}>
        <h2 id="segments-heading" className={styles.cardTitle}>
          Segments
        </h2>
        <span className={styles.cardSubtitle}>
          <SegmentProgress
            current={currentSegment}
            total={segments.length}
            highlightCurrent={status === "ACTIVE"}
          />
        </span>
      </header>
      {segments.length === 0 ? (
        <div className={styles.empty}>No segments configured.</div>
      ) : (
        <ul className={styles.segmentList}>
          {segments.map((seg) => {
            const isCurrent =
              status === "ACTIVE" && seg.segmentNumber === currentSegment;
            return (
              <li
                key={seg.id}
                className={`${styles.segmentRow} ${
                  isCurrent ? styles.segmentRowCurrent : ""
                }`}
              >
                <span className={styles.segmentNumber}>
                  W{seg.segmentNumber}
                </span>
                <span className={styles.segmentTitle}>{seg.title}</span>
                <span className={styles.segmentRange}>{segmentRange(seg)}</span>
                {isCurrent ? (
                  <span className={styles.currentBadge}>Current</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function MembersSection({
  members,
}: {
  members: StaffRoomDetailMember[];
}) {
  return (
    <section className={styles.card} aria-labelledby="members-heading">
      <header className={styles.cardHead}>
        <h2 id="members-heading" className={styles.cardTitle}>
          Members
        </h2>
        <span className={styles.cardSubtitle}>{members.length}</span>
      </header>
      {members.length === 0 ? (
        <div className={styles.empty}>No members yet.</div>
      ) : (
        <div>
          {members.map((m) => (
            <div
              key={m.id}
              className={styles.memberRow}
              data-testid="member-row"
            >
              <span className={styles.memberName}>
                {m.displayName ?? m.username ?? "Unknown reader"}
                {m.isFacilitator ? (
                  <span
                    className={styles.facilitatorBadgeInline}
                    aria-label="Facilitator"
                  >
                    Facilitator
                  </span>
                ) : null}
              </span>
              <span className={styles.memberMeta}>
                joined {shortDate(m.joinedAt)} · progress {m.progress}
                {m.lastActivity
                  ? ` · last seen ${shortDate(m.lastActivity)}`
                  : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function MargauxPostsSection({ posts }: { posts: StaffMargauxPost[] }) {
  return (
    <section className={styles.card} aria-labelledby="margaux-heading">
      <header className={styles.cardHead}>
        <h2 id="margaux-heading" className={styles.cardTitle}>
          Recent Margaux posts
        </h2>
        <span className={styles.cardSubtitle}>{posts.length}</span>
      </header>
      {posts.length === 0 ? (
        <div className={styles.empty}>Margaux hasn't posted in this room.</div>
      ) : (
        <div>
          {posts.slice(0, 8).map((p) => (
            <div key={p.id} className={styles.postRow}>
              <div className={styles.postMeta}>
                <span>
                  W{p.segmentNumber ?? "?"} · {dateAndTime(p.createdAt)}
                </span>
                {p.isRecap ? (
                  <span className={styles.recapTag}>Recap</span>
                ) : null}
              </div>
              <div className={styles.postBody}>{p.reflectionText}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function SchedulerLogSection({
  entries,
}: {
  entries: StaffSchedulerLogEntry[];
}) {
  return (
    <section className={styles.card} aria-labelledby="scheduler-heading">
      <header className={styles.cardHead}>
        <h2 id="scheduler-heading" className={styles.cardTitle}>
          Scheduler
        </h2>
        <span className={styles.cardSubtitle}>last fired-at per job</span>
      </header>
      {entries.length === 0 ? (
        <div className={styles.empty}>No jobs have fired yet.</div>
      ) : (
        <div>
          {entries.map((e) => (
            <div key={e.jobName} className={styles.scheduleRow}>
              <span className={styles.jobName}>{e.jobName}</span>
              <span className={styles.jobMeta}>
                {e.segmentNumber ? `W${e.segmentNumber} · ` : ""}
                {dateAndTime(e.lastFiredAt)}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function ReflectionsSection({
  reflections,
  onReply,
  onHide,
  hidingReflectionId,
  hideableForUserId,
}: {
  reflections: StaffReflection[];
  onReply?: (reflectionId: string) => void;
  onHide?: (reflectionId: string) => void;
  hidingReflectionId?: string | null;
  /** When set, the Hide button only shows on reflections NOT authored by this user. */
  hideableForUserId?: string | null;
}) {
  return (
    <section className={styles.card} aria-labelledby="reflections-heading">
      <header className={styles.cardHead}>
        <h2 id="reflections-heading" className={styles.cardTitle}>
          Recent reflections
        </h2>
        <span className={styles.cardSubtitle}>{reflections.length}</span>
      </header>
      {reflections.length === 0 ? (
        <div className={styles.empty}>No reader reflections yet.</div>
      ) : (
        <div>
          {reflections.slice(0, 10).map((r) => {
            const canHide =
              Boolean(onHide) &&
              (!hideableForUserId || r.user.id !== hideableForUserId);
            return (
              <div key={r.id} className={styles.postRow}>
                <div className={styles.postMeta}>
                  <span>
                    {r.user.displayName ?? r.user.username ?? "Reader"} · W
                    {r.segmentNumber ?? "?"} · {dateAndTime(r.createdAt)}
                  </span>
                  {r.isFacilitatorBadge ? (
                    <span
                      className={styles.facilitatorBadge}
                      aria-label="Facilitator"
                    >
                      Facilitator
                    </span>
                  ) : null}
                </div>
                <div className={styles.postBody}>{r.reflectionText}</div>
                {onReply || canHide ? (
                  <div className={styles.postActions}>
                    {onReply ? (
                      <button
                        type="button"
                        className={styles.replyButton}
                        onClick={() => onReply(r.id)}
                      >
                        Reply
                      </button>
                    ) : null}
                    {canHide ? (
                      <button
                        type="button"
                        className={styles.hideButton}
                        onClick={() => onHide?.(r.id)}
                        disabled={hidingReflectionId === r.id}
                        aria-label="Soft-hide this reflection"
                      >
                        {hidingReflectionId === r.id ? "Hiding…" : "Hide"}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
